import { ApplicableCouponsRequest } from '@/src/types/applicableCoupons'
import { FlightCard, JsonsData, Sectors } from '@/src/types/flight'
import { QueryInput } from '@/src/types/searchQuery'
import { convertDDMMYYYYToISO } from '@/src/utils/date/convertDateFormat'

interface GetApplicableCouponsRequestParams {
    flights: FlightCard[]
    sectors: Sectors
    jsons: JsonsData
    sid: string
    urlQueryObj: Partial<QueryInput>
    totalFare: number
    baseFare: number
    fareCategory: string
}

const getDateTimeString = (date: string, time: string): string => {
    if (!date || !time) return ''
    return `${convertDDMMYYYYToISO(date)}T${time}:00`
}

export const getApplicableCouponsRequest = ({
    flights,
    sectors,
    jsons,
    sid,
    urlQueryObj,
    totalFare,
    baseFare,
    fareCategory
}: GetApplicableCouponsRequestParams): ApplicableCouponsRequest | null => {
    if (!flights?.length) return null

    const flightParamsMap: ApplicableCouponsRequest['flightParamsMap'] = {}

    flights.forEach((flight, index) => {
        const sector = sectors?.[flight.sectorKeys?.[0]]
        const segments = sector?.flights?.segments || []
        if (!segments.length) return

        const fare = flight.priceBreakup?.fare
        const bookingClasses = fare?.bc?.split(',') || []
        const flightNumbers = segments.map(segment => Number(segment.flightNumber?.split('-')?.[1] || 0))

        flightParamsMap[String(index + 1)] = {
            departureDate: getDateTimeString(segments[0].departure.date, segments[0].departure.time),
            airlines: segments.map(segment => segment.flightNumber?.split('-')?.[0] || ''),
            flightNumbers,
            segmentCount: segments.length,
            fareBasisCode: fare?.fb ? [fare.fb] : [],
            fareCategory: [fareCategory],
            fareClass: segments.map((_, segmentIndex) => bookingClasses[segmentIndex] || bookingClasses[0] || ''),
            cabinType: (segments[0].flightClass || urlQueryObj.class || 'ECONOMY').toUpperCase()
        }
    })

    if (!Object.keys(flightParamsMap).length) return null

    const firstSector = sectors?.[flights[0]?.sectorKeys?.[0]]
    const lastFlight = flights[flights.length - 1]
    const lastSector = sectors?.[lastFlight?.sectorKeys?.[lastFlight?.sectorKeys?.length - 1]]
    const firstSegment = firstSector?.flights?.segments?.[0]
    const lastSegment = lastSector?.flights?.segments?.[lastSector.flights.segments.length - 1]

    const partnerId = Number(jsons?.userInfo?.partnerId || 0)
    const channelId: number | '' = partnerId === 0 ? '' : partnerId

    return {
        flightParamsMap,
        paxInfo: {
            adults: 1,
            children: Number(urlQueryObj.childs || 0),
            infants: Number(urlQueryObj.infants || 0)
        },
        searchCriteria: {
            origin: firstSegment?.departure?.airportCode || urlQueryObj.from || '',
            destination: lastSegment?.arrival?.airportCode || urlQueryObj.to || '',
            totalFare,
            baseFare,
            sid: sid || '',
            sellingCountry: jsons?.searchType?.sellingCountry || '',
            trafficChannel: 'B2C',
            channelId
        }
    }
}

